import { FunctionComponent, useState } from "react";
import { ExpandMore, ChevronRight } from "@mui/icons-material";
import { INestedSchema } from "data/types";
import { Text } from "components/text";
import styles from "./NestedSchemaList.module.css";

export interface SchemaLeaf {
  name: string;
  key: string;
}

interface NestedSchemaListProps {
  schema: INestedSchema;
  onClick?: (leaf: SchemaLeaf) => void;
  renderLeafIcon?: (leaf: SchemaLeaf) => React.ReactNode;
  isIconAlwaysVisible?: (leaf: SchemaLeaf) => boolean;
}

interface NestedSchemaNodeProps {
  label: string;
  node: INestedSchema;
  onClick?: (leaf: SchemaLeaf) => void;
  renderLeafIcon?: (leaf: SchemaLeaf) => React.ReactNode;
  isIconAlwaysVisible?: (leaf: SchemaLeaf) => boolean;
}

const LeafItem: FunctionComponent<{
  leaf: SchemaLeaf;
  onClick?: (leaf: SchemaLeaf) => void;
  renderLeafIcon?: (leaf: SchemaLeaf) => React.ReactNode;
  isIconAlwaysVisible?: (leaf: SchemaLeaf) => boolean;
}> = ({ leaf, onClick, renderLeafIcon, isIconAlwaysVisible }) => {
  const icon = renderLeafIcon?.(leaf);
  const iconAlwaysVisible = isIconAlwaysVisible?.(leaf) ?? false;
  return (
    <div
      className={`${styles.leaf}${onClick ? ` ${styles.clickable}` : ""}`}
      onClick={() => onClick?.(leaf)}
    >
      <Text size="small">{leaf.name}</Text>
      {icon && (
        <span className={`${styles.leafIcon}${iconAlwaysVisible ? ` ${styles.leafIconAlwaysVisible}` : ""}`}>
          {icon}
        </span>
      )}
    </div>
  );
};

const NestedSchemaNode: FunctionComponent<NestedSchemaNodeProps> = ({
  label, node, onClick, renderLeafIcon, isIconAlwaysVisible,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={styles.group}>
      <div className={styles.groupHeader} onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}>
        {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
        <Text size="small">{label}</Text>
      </div>
      {expanded && (
        <div className={styles.groupChildren}>
          {node.children.map(child => (
            <NestedSchemaNode
              key={child.key}
              label={child.key}
              node={child.value}
              onClick={onClick}
              renderLeafIcon={renderLeafIcon}
              isIconAlwaysVisible={isIconAlwaysVisible}
            />
          ))}
          {node.schema.map(leaf => (
            <LeafItem key={leaf.key} leaf={leaf} onClick={onClick} renderLeafIcon={renderLeafIcon} isIconAlwaysVisible={isIconAlwaysVisible} />
          ))}
        </div>
      )}
    </div>
  );
};

export const NestedSchemaList: FunctionComponent<NestedSchemaListProps> = ({
  schema, onClick, renderLeafIcon, isIconAlwaysVisible,
}) => {
  return (
    <div className={styles.container}>
      {schema.children.map(child => (
        <NestedSchemaNode
          key={child.key}
          label={child.key}
          node={child.value}
          onClick={onClick}
          renderLeafIcon={renderLeafIcon}
          isIconAlwaysVisible={isIconAlwaysVisible}
        />
      ))}
      {schema.schema.map(leaf => (
        <LeafItem key={leaf.key} leaf={leaf} onClick={onClick} renderLeafIcon={renderLeafIcon} isIconAlwaysVisible={isIconAlwaysVisible} />
      ))}
    </div>
  );
};
